/**
 * Session 19c — OCPP 2.0.1 outbound Smart Charging messages (CP → CSMS):
 *   - ReportChargingProfiles — paired reply to GetChargingProfiles.
 *   - NotifyChargingLimit    — an external limit overrode the schedule.
 *   - ClearedChargingLimit   — a previously-notified external limit cleared.
 *
 * These are v2.0.1-only; they live as OcppV201Adapter methods (like
 * NotifyReport), not on the version-neutral OcppAdapter interface.
 */
import type { ChargingProfileSnapshot } from '@/domain/ChargingProfile';

/** Serialise a domain ChargingProfileSnapshot to the v2.0.1 ChargingProfileType. */
export function domainProfileToV201(p: ChargingProfileSnapshot): Record<string, unknown> {
  const schedule: Record<string, unknown> = {
    id: 0,
    chargingRateUnit: p.schedule.chargingRateUnit,
    chargingSchedulePeriod: p.schedule.chargingSchedulePeriod.map((per) => ({
      startPeriod: per.startPeriod,
      limit: per.limit,
      ...(per.numberPhases !== undefined ? { numberPhases: per.numberPhases } : {}),
    })),
    ...(p.schedule.duration !== undefined ? { duration: p.schedule.duration } : {}),
    ...(p.schedule.minChargingRate !== undefined
      ? { minChargingRate: p.schedule.minChargingRate }
      : {}),
    ...(p.schedule.startSchedule !== undefined
      ? { startSchedule: new Date(p.schedule.startSchedule).toISOString() }
      : {}),
  };
  return {
    id: p.id,
    stackLevel: p.stackLevel,
    // Emit the v2.0.1 wire name for the station-wide ceiling (domain uses the
    // v16 "ChargePointMaxProfile"); the response schema's enum requires it.
    chargingProfilePurpose:
      p.purpose === 'ChargePointMaxProfile' ? 'ChargingStationMaxProfile' : p.purpose,
    chargingProfileKind: p.kind,
    chargingSchedule: [schedule],
    ...(p.recurrencyKind !== undefined ? { recurrencyKind: p.recurrencyKind } : {}),
    ...(p.validFrom !== undefined ? { validFrom: new Date(p.validFrom).toISOString() } : {}),
    ...(p.validTo !== undefined ? { validTo: new Date(p.validTo).toISOString() } : {}),
    // v2.0.1 transactionId is a string.
    ...(p.transactionId !== undefined ? { transactionId: String(p.transactionId) } : {}),
  };
}

export interface ReportChargingProfilesDomain {
  readonly requestId: number;
  readonly chargingLimitSource: string; // EMS | Other | SO | CSO
  readonly evseId: number;
  readonly tbc: boolean;
  readonly chargingProfile: readonly ChargingProfileSnapshot[];
}

export function buildReportChargingProfilesRequest(
  req: ReportChargingProfilesDomain,
): Record<string, unknown> {
  return {
    requestId: req.requestId,
    chargingLimitSource: req.chargingLimitSource,
    evseId: req.evseId,
    tbc: req.tbc,
    chargingProfile: req.chargingProfile.map(domainProfileToV201),
  };
}

/**
 * Split a profile set into paginated ReportChargingProfiles payloads
 * (mirrors paginateNotifyReport). Empty input → one terminating page.
 */
export function paginateReportChargingProfiles(
  requestId: number,
  evseId: number,
  chargingLimitSource: string,
  profiles: readonly ChargingProfileSnapshot[],
  maxItemsPerPage: number,
): readonly ReportChargingProfilesDomain[] {
  const perPage = Math.max(1, Math.floor(maxItemsPerPage));
  if (profiles.length === 0) {
    return [{ requestId, chargingLimitSource, evseId, tbc: false, chargingProfile: [] }];
  }
  const pages: ReportChargingProfilesDomain[] = [];
  for (let i = 0; i < profiles.length; i += perPage) {
    pages.push({
      requestId,
      chargingLimitSource,
      evseId,
      tbc: i + perPage < profiles.length,
      chargingProfile: profiles.slice(i, i + perPage),
    });
  }
  return pages;
}

export interface NotifyChargingLimitDomain {
  readonly chargingLimitSource: string;
  readonly isGridCritical?: boolean;
  readonly evseId?: number;
}

export function buildNotifyChargingLimitRequest(
  req: NotifyChargingLimitDomain,
): Record<string, unknown> {
  return {
    chargingLimit: {
      chargingLimitSource: req.chargingLimitSource,
      ...(req.isGridCritical !== undefined ? { isGridCritical: req.isGridCritical } : {}),
    },
    ...(req.evseId !== undefined ? { evseId: req.evseId } : {}),
  };
}

export interface ClearedChargingLimitDomain {
  readonly chargingLimitSource: string;
  readonly evseId?: number;
}

export function buildClearedChargingLimitRequest(
  req: ClearedChargingLimitDomain,
): Record<string, unknown> {
  return {
    chargingLimitSource: req.chargingLimitSource,
    ...(req.evseId !== undefined ? { evseId: req.evseId } : {}),
  };
}
